import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { ChartBar, Download, Play, Plus, X as XIcon, Funnel } from "@phosphor-icons/react";

export default function Reports() {
  const [schema, setSchema] = useState(null);
  const [resource, setResource] = useState("cases");
  const [selFields, setSelFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [groupBy, setGroupBy] = useState("");
  const [sort, setSort] = useState("");
  const [limit, setLimit] = useState(200);
  const [rows, setRows] = useState(null);
  const [groupedBy, setGroupedBy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => { api.get("/reports/schema").then(({ data }) => setSchema(data)); }, []);

  const meta = schema?.[resource];
  useEffect(() => {
    if (meta) {
      setSelFields(meta.fields.slice(0, 6));
      setFilters({}); setGroupBy(""); setSort(""); setRows(null); setGroupedBy(null);
    }
  }, [resource, schema]);  // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (format = "json") => {
    if (!meta) return;
    setBusy(true); setErr(null);
    try {
      const payload = {
        resource, fields: selFields, filters,
        group_by: groupBy || null, sort: sort || null,
        limit: Number(limit) || 200, format,
      };
      if (format === "csv") {
        const res = await api.post("/reports/run", payload, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `report_${resource}.csv`;
        document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      } else {
        const { data } = await api.post("/reports/run", payload);
        setRows(data.rows); setGroupedBy(data.grouped_by || null);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const toggleField = (f) => setSelFields((cur) => cur.includes(f) ? cur.filter((x) => x !== f) : [...cur, f]);
  const setFilter = (k, v) => setFilters((cur) => ({ ...cur, [k]: v || undefined }));

  const headers = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    if (groupedBy) {
      return Object.keys(rows[0]);
    }
    return selFields;
  }, [rows, selFields, groupedBy]);

  if (!schema) return <div className="text-[12px] text-[var(--span-text-2)]">Loading report builder…</div>;

  return (
    <div className="space-y-5" data-testid="reports-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Analytics</div>
          <h1 className="font-display text-[28px] font-bold tracking-tight mt-1">Reports & exports</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">Universal multi-format exports + ad-hoc builder.</div>
        </div>
      </div>

      <QuickExportsPanel />

      <div className="grid grid-cols-12 gap-5">
        {/* Left: builder */}
        <div className="col-span-12 lg:col-span-4 space-y-4">
          <div className="card-flat p-4">
            <label className="label-eyebrow block mb-2">1 · Resource</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(schema).map((r) => (
                <button
                  key={r}
                  data-testid={`resource-${r}`}
                  onClick={() => setResource(r)}
                  className={`px-3 py-2 text-[12px] hairline ${resource === r ? "bg-black text-white" : "surface"}`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="card-flat p-4">
            <label className="label-eyebrow block mb-2">2 · Fields</label>
            <div className="flex flex-wrap gap-1.5" data-testid="field-chips">
              {meta.fields.map((f) => (
                <button
                  key={f}
                  data-testid={`field-${f}`}
                  onClick={() => toggleField(f)}
                  className={`px-2 py-1 text-[11px] font-mono hairline ${selFields.includes(f) ? "bg-black text-white" : "surface text-[var(--span-text-2)]"}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {meta.filters.length > 0 && (
            <div className="card-flat p-4">
              <label className="label-eyebrow flex items-center gap-1 mb-2"><Funnel size={11}/> 3 · Filters</label>
              <div className="space-y-2">
                {meta.filters.map((f) => (
                  <div key={f} className="flex items-center gap-2">
                    <span className="text-[11px] font-mono w-24 text-[var(--span-text-2)]">{f}</span>
                    <input
                      data-testid={`filter-${f}`}
                      value={filters[f] || ""}
                      onChange={(e) => setFilter(f, e.target.value)}
                      placeholder="any"
                      className="span-input flex-1 text-[12px]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="card-flat p-4 space-y-3">
            <div>
              <label className="label-eyebrow block mb-1">4 · Group by</label>
              <select data-testid="group-by" value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="span-input">
                <option value="">— No grouping (raw rows) —</option>
                {meta.groupable.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Sort</label>
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="span-input" disabled={!!groupBy}>
                <option value="">— Default —</option>
                {meta.fields.flatMap((f) => [
                  <option key={`${f}-asc`} value={f}>{`${f} (asc)`}</option>,
                  <option key={`${f}-desc`} value={`-${f}`}>{`${f} (desc)`}</option>,
                ])}
              </select>
            </div>
            <div>
              <label className="label-eyebrow block mb-1">Limit</label>
              <input type="number" max={1000} min={1} value={limit} onChange={(e) => setLimit(e.target.value)} className="span-input"/>
            </div>
          </div>

          <div className="flex gap-2">
            <button data-testid="run-report-btn" onClick={() => run("json")} disabled={busy} className="span-btn flex-1">
              <Play size={12}/> Run
            </button>
            <button data-testid="export-csv-btn" onClick={() => run("csv")} disabled={busy} className="span-btn span-btn-ghost">
              <Download size={12}/> CSV
            </button>
          </div>
        </div>

        {/* Right: results */}
        <div className="col-span-12 lg:col-span-8">
          <div className="card-flat min-h-[420px]">
            {err && (
              <div className="p-4 border-l-4 border-[var(--span-danger)] bg-red-50 dark:bg-red-950 text-[12px]" data-testid="reports-error">
                {String(err)}
              </div>
            )}
            {!rows && !err && (
              <div className="p-10 text-center text-[12px] text-[var(--span-text-2)]">
                <ChartBar size={32} weight="duotone" className="mx-auto mb-2 opacity-50"/>
                Configure & click <span className="font-mono">Run</span>.
              </div>
            )}
            {rows && rows.length === 0 && (
              <div className="p-10 text-center text-[12px] text-[var(--span-text-2)]">No rows match.</div>
            )}
            {rows && rows.length > 0 && (
              <>
                <div className="px-5 py-3 border-b border-[var(--span-border)] flex items-center justify-between text-[12px]">
                  <div className="font-mono">
                    {rows.length} {groupedBy ? `groups by ${groupedBy}` : "rows"} · {resource}
                  </div>
                </div>
                {groupedBy ? <GroupedTable rows={rows} headers={headers} groupedBy={groupedBy}/> : <FlatTable rows={rows} headers={headers}/>}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FlatTable({ rows, headers }) {
  return (
    <div className="overflow-auto max-h-[60vh]">
      <table className="span-table">
        <thead><tr>{headers.map((h) => <th key={h} className="font-mono text-[10px] uppercase">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`report-row-${i}`}>
              {headers.map((h) => <td key={h} className="font-mono text-[11px]">{fmt(r[h])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GroupedTable({ rows, headers, groupedBy }) {
  const maxCount = Math.max(...rows.map((r) => r.count || 0), 1);
  return (
    <div className="p-5 space-y-2 max-h-[60vh] overflow-auto">
      {rows.map((r, i) => (
        <div key={i} className="space-y-1" data-testid={`group-row-${i}`}>
          <div className="flex items-center justify-between text-[12px]">
            <div className="font-medium">{String(r[groupedBy] ?? "—")}</div>
            <div className="font-mono text-[var(--span-text-2)]">
              {r.count} {r.sum !== undefined && <>· Σ {Number(r.sum).toLocaleString()}</>}
              {r.avg !== undefined && <> · μ {Number(r.avg).toFixed(0)}</>}
            </div>
          </div>
          <div className="h-1.5 bg-[var(--span-surface-2)]">
            <div className="h-full bg-black dark:bg-white" style={{ width: `${(r.count / maxCount) * 100}%` }}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "number") return v.toLocaleString();
  if (typeof v === "string" && v.length > 60) return v.slice(0, 57) + "…";
  return String(v);
}

const UNIVERSAL_RESOURCES = [
  { r: "cases",         label: "Cases",         formats: ["csv","xlsx","docx","pdf","pptx"] },
  { r: "sightings",     label: "Sightings",     formats: ["csv","xlsx","docx","pdf","pptx"] },
  { r: "invoices",      label: "Invoices",      formats: ["csv","xlsx","docx","pdf"] },
  { r: "expenses",      label: "Expenses",      formats: ["csv","xlsx","docx","pdf"] },
  { r: "clients",       label: "Clients",       formats: ["csv","xlsx","docx","pdf","pptx"] },
  { r: "suppliers",     label: "Suppliers",     formats: ["csv","xlsx","docx","pdf","pptx"] },
  { r: "receivables",   label: "Receivables",   formats: ["csv","xlsx","docx","pdf","pptx"] },
  { r: "stakeholders",  label: "Stakeholders",  formats: ["csv","xlsx","docx","pdf"] },
  { r: "audit-log",     label: "Audit log",     formats: ["csv","xlsx","docx","pdf"] },
  { r: "legal-holds",   label: "Legal holds",   formats: ["csv","xlsx","docx","pdf"] },
  { r: "analytics-ceo", label: "CEO snapshot",  formats: ["xlsx","docx","pdf","pptx"] },
];

function QuickExportsPanel() {
  const [busy, setBusy] = useState(null);
  const download = async (r, f) => {
    setBusy(`${r}.${f}`);
    try {
      const { data } = await api.get(`/reports/${r}.${f}`, { responseType: "blob" });
      const blob = new Blob([data]);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `span_${r}.${f}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      alert("Export failed: " + (e?.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };
  return (
    <div className="card-flat p-5" data-testid="quick-exports">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="label-eyebrow">Quick exports</div>
          <div className="font-bold text-[15px]">Universal multi-format downloads</div>
        </div>
        <div className="text-[10px] text-[var(--span-text-2)] uppercase tracking-wider">11 resources · 5 formats</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {UNIVERSAL_RESOURCES.map(({ r, label, formats }) => (
          <div key={r} className="flex items-center justify-between gap-2 p-2 bg-[var(--surface-2)] rounded" data-testid={`qx-${r}`}>
            <div className="text-[12px] font-semibold flex-1 truncate">{label}</div>
            <div className="flex gap-0.5">
              {formats.map(f => (
                <button
                  key={f}
                  data-testid={`qx-${r}-${f}`}
                  disabled={busy === `${r}.${f}`}
                  onClick={() => download(r, f)}
                  className="text-[10px] uppercase font-mono px-1.5 py-1 hover:bg-[var(--surface)] rounded disabled:opacity-50">
                  {busy === `${r}.${f}` ? "…" : f}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

